import { z } from 'zod';
import { boardMetricKeySchema, currencySchema, divisionSchema, kpiSourceSchema, kpiStatusSchema, pillarSchema, unitSchema, valueTypeSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
export const KPI_NAME_MAX_LENGTH = 160;
export const KPI_CODE_MAX_LENGTH = 32;
export const KPI_DATA_SOURCE_MAX_LENGTH = 160;
export const KPI_DESCRIPTION_MAX_LENGTH = 2000;
export const KPI_METHOD_MAX_LENGTH = 120;
/** `SELL-SOLD`, `SRV-LABOUR-REV`: upper-case letters and digits in dash-separated words. */
export const kpiCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(KPI_CODE_MAX_LENGTH, `Use at most ${KPI_CODE_MAX_LENGTH} characters`)
    .regex(/^[A-Z0-9]+(-[A-Z0-9]+)*$/, 'Use upper-case letters, digits and single dashes (e.g. SELL-SOLD)');
export const formulaNodeSchema = z.lazy(() => z.discriminatedUnion('type', [
    z.object({ type: z.literal('kpi'), kpiId: uuidSchema }).strict(),
    z.object({ type: z.literal('const'), value: z.number().finite() }).strict(),
    z.object({ type: z.literal('binary'), op: z.enum(['+', '-', '*', '/']), left: formulaNodeSchema, right: formulaNodeSchema }).strict(),
]));
export const kpiSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    pillar: pillarSchema,
    division: divisionSchema.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    /** Alcaraz's `method`, e.g. "End-of-week balance". */
    measurementMethod: z.string().nullable(),
    unit: unitSchema,
    /** Set if and only if the unit is CURRENCY. */
    currency: currencySchema.nullable(),
    valueType: valueTypeSchema,
    source: kpiSourceSchema,
    /** Free-text label for where the numbers come from (e.g. "CRM"); distinct from `source`. */
    dataSource: z.string().nullable(),
    /** Same-pillar parent; display-only hierarchy. */
    parentKpiId: uuidSchema.nullable(),
    displayOrder: z.number().int(),
    status: kpiStatusSchema,
    /** A calculated KPI's formula; `null` for a manual KPI and for a restricted one (never serialised). */
    formula: formulaNodeSchema.nullable(),
    /** A calculated KPI whose formula reaches into a pillar the caller cannot see: blank everywhere, formula withheld. */
    restricted: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const deletionBlockerCodeSchema = z.enum(['HISTORY', 'FORMULA_DEPENDENCY', 'TARGET_LINK', 'HAS_CHILDREN']);
export const deletionBlockerSchema = z.object({
    code: deletionBlockerCodeSchema,
    message: z.string(),
});
export const deletionAssessmentSchema = z.object({
    deletable: z.boolean(),
    blockers: z.array(deletionBlockerSchema),
});
/** `kpis.get` adds the guards the Setup dialogs show before the user confirms anything. */
export const kpiDetailSchema = kpiSchema.extend({
    /** What `kpis.delete` would answer right now; the delete re-checks in its own transaction. */
    deletion: deletionAssessmentSchema,
    /** Shown (never blocking) before deactivating a KPI that has sub-KPIs or feeds formulas; `null` when nothing applies. */
    deactivationWarning: z.string().nullable(),
});
export const listKpisInputSchema = z.object({
    pillar: pillarSchema.optional(),
    search: z.string().trim().max(120).optional(),
    includeInactive: z.boolean().default(false),
});
export const listKpisOutputSchema = z.object({
    /** Visible pillars only: pillar order, then parents before their sub-KPIs (`depth` 0 = root), then display order. */
    items: z.array(kpiSchema.extend({ depth: z.number().int().min(0) })),
});
export const getKpiInputSchema = z.object({ id: uuidSchema });
const optionalText = (max) => z.string().trim().max(max, `Use at most ${max} characters`).nullable().optional();
/** The editable fields; `create` and `update` share them so the rules cannot drift. */
const kpiFieldsSchema = z.object({
    pillar: pillarSchema,
    division: divisionSchema.nullable().optional(),
    name: z.string().trim().min(1, 'Name is required').max(KPI_NAME_MAX_LENGTH, `Use at most ${KPI_NAME_MAX_LENGTH} characters`),
    description: optionalText(KPI_DESCRIPTION_MAX_LENGTH),
    measurementMethod: optionalText(KPI_METHOD_MAX_LENGTH),
    unit: unitSchema,
    currency: currencySchema.nullable().optional(),
    valueType: valueTypeSchema,
    source: kpiSourceSchema,
    /**
     * Required when the source is CALCULATED (on create, and on an update that switches a KPI to Calculated);
     * ignored for a manual KPI. Every save of a formula rewrites the KPI's dependency rows.
     */
    formula: formulaNodeSchema.nullable().optional(),
    dataSource: optionalText(KPI_DATA_SOURCE_MAX_LENGTH),
    parentKpiId: uuidSchema.nullable().optional(),
    status: kpiStatusSchema,
    /** The author has seen the formula warnings of a previous 409 and saves anyway. */
    confirmWarnings: z.boolean().optional(),
});
const CURRENCY_REQUIRED_MESSAGE = 'A currency is required when the unit is Currency.';
export const createKpiInputSchema = kpiFieldsSchema
    .extend({
    /** Generated from the pillar and name when omitted (`SELL-VEHICLES-SOLD`). */
    code: kpiCodeSchema.optional(),
    valueType: valueTypeSchema.default('ABSOLUTE'),
    source: kpiSourceSchema.default('MANUAL'),
    status: kpiStatusSchema.default('ACTIVE'),
})
    .refine((input) => input.unit !== 'CURRENCY' || Boolean(input.currency), { path: ['currency'], message: CURRENCY_REQUIRED_MESSAGE });
/** Only the supplied fields change. The code is immutable; changing the unit away from Currency clears the currency. */
export const updateKpiInputSchema = kpiFieldsSchema.partial().extend({ id: uuidSchema });
export const reorderKpisInputSchema = z.object({
    pillar: pillarSchema,
    /** Exactly the pillar's current KPI ids (active and inactive) in the new order. */
    orderedIds: z.array(uuidSchema).min(1, 'A non-empty ordered list of KPI ids is required.'),
});
export const reorderKpisOutputSchema = z.object({
    pillar: pillarSchema,
    items: z.array(z.object({ id: uuidSchema, displayOrder: z.number().int().min(1) })),
});
export const deleteKpiInputSchema = z.object({ id: uuidSchema });
export const deleteKpiOutputSchema = z.object({
    id: uuidSchema,
    deleted: z.literal(true),
});
/** The body of the 409 `kpis.delete` answers with when anything blocks the delete. */
export const kpiDeletionConflictSchema = z.object({
    message: z.string(),
    blockers: z.array(deletionBlockerSchema),
});
export const formulaWarningCodeSchema = z.enum(['AMBIGUOUS_PERCENT', 'PLAIN_NUMBER_MIX', 'INACTIVE_SOURCE', 'VALUE_TYPE_MIX', 'CONVERSION']);
export const formulaWarningSchema = z.object({
    code: formulaWarningCodeSchema,
    message: z.string(),
});
/**
 * The body of the 409 `kpis.create` / `kpis.update` answer with when a formula is valid but ambiguous: nothing was
 * saved; repeating the request with `confirmWarnings: true` saves it.
 */
export const kpiFormulaConflictSchema = z.object({
    message: z.string(),
    needsConfirmation: z.literal(true),
    warnings: z.array(formulaWarningSchema),
});
/**
 * The body of the 409 `kpis.update` answers with when the unit or currency of a KPI that feeds the board would change:
 * nothing was saved; unbind the keys in Setup → Board bindings first (never silently unlinked).
 */
export const kpiBoardBoundConflictSchema = z.object({
    message: z.string(),
    boundKeys: z.array(boardMetricKeySchema),
});
//# sourceMappingURL=kpis.schemas.js.map